import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { CancelToken, isCancel } from 'axios';
import addDays from 'date-fns/addDays';
import isBefore from 'date-fns/isBefore';
import isSameDay from 'date-fns/isSameDay';
import isValid from 'date-fns/isValid';
import isWithinInterval from 'date-fns/isWithinInterval';
import subDays from 'date-fns/subDays';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch, faMinus } from '@fortawesome/free-solid-svg-icons';

import UtilizationService from '../../Services/UtilizationService';

import 'react-datepicker/dist/react-datepicker.css';

export default memo(RerunOutputSignalsModal);

const initState = {
  endDate: null,
  error: '',
  requestPending: false,
  startDate: null,
};
const maxDays = 30;
const today = new Date();

function RerunOutputSignalsModal(props) {
  const [state, updateState] = useState(initState);
  const { assetID } = useParams();

  const source = CancelToken.source();
  const utilService = useMemo(() => new UtilizationService(), []);

  const maxDateEnd = useMemo(() => {
    if (!isValid(state.startDate)) return today;

    const startPlusMax = addDays(state.startDate, maxDays);
    return isBefore(startPlusMax, today) ? startPlusMax : today;
  }, [state.startDate]);

  const maxDateStart = useMemo(
    () => subDays(state.endDate || today, 1),
    [state.endDate]
  );

  const minDateEnd = useMemo(
    () => (isValid(state.startDate) ? addDays(state.startDate, 1) : undefined),
    [state.startDate]
  );

  const minDateStart = useMemo(
    () =>
      isValid(state.endDate) ? subDays(state.endDate, maxDays) : undefined,
    [state.endDate]
  );

  const handleEndDateSelect = useCallback((endDate) => {
    updateState((prevState) => ({ ...prevState, endDate, error: '' }));
  }, []);

  const handleStartDateSelect = useCallback((startDate) => {
    updateState((prevState) => ({ ...prevState, startDate, error: '' }));
  }, []);

  const handleRun = async () => {
    try {
      updateState((prevState) => ({ ...prevState, requestPending: true }));
      const { startDate, endDate } = validateDays(state);
      const params = { endDate, rulesProcessor: false, startDate };
      await utilService.runGatewayOutputSignals(assetID, params, source.token);
      props.handleOutputSignalRerunSuccess();
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        requestPending: false,
      }));
    }
  };

  const submitDisabled = useMemo(
    () =>
      state.requestPending ||
      !isValid(state.endDate) ||
      !isValid(state.startDate),
    [state.endDate, state.requestPending, state.startDate]
  );

  useEffect(() => source.cancel, []);

  return (
    <Modal
      backdrop={state.requestPending ? 'static' : true}
      centered
      contentClassName="rounded-lg"
      isOpen={props.isOpen}
      size="lg"
      toggle={state.requestPending ? () => null : props.toggleModal}
    >
      <ModalHeader
        toggle={state.requestPending ? () => null : props.toggleModal}
      >
        Rerun Calculated Signals
      </ModalHeader>
      <ModalBody className="container-fluid py-md-5 nine-tenth-text">
        <div className="row align-items-center justify-content-center">
          <div className="col col-md-5 d-flex align-items-center justify-content-end">
            <div className="form-group d-flex flex-column">
              <label htmlFor="startDate">Start Date</label>
              <DatePicker
                autoComplete="off"
                endDate={state.endDate}
                isClearable={!!state.startDate}
                maxDate={maxDateStart}
                minDate={minDateStart}
                name="startDate"
                onChange={handleStartDateSelect}
                openToDate={maxDateStart}
                placeholderText="From"
                selected={state.startDate}
                selectsStart
                startDate={state.startDate}
              />
            </div>
          </div>
          <div className="d-none d-md-block col-md-2 text-center">
            <FontAwesomeIcon icon={faMinus} size="lg" />
          </div>
          <div className="col col-md-5 d-flex align-items-center justify-content-start">
            <div className="form-group d-flex flex-column">
              <label htmlFor="endDate">End Date</label>
              <DatePicker
                autoComplete="off"
                endDate={state.endDate}
                isClearable={!!state.endDate}
                maxDate={maxDateEnd}
                minDate={minDateEnd}
                name="endDate"
                onChange={handleEndDateSelect}
                openToDate={maxDateEnd}
                placeholderText="To"
                selected={state.endDate}
                selectsEnd
                startDate={state.startDate}
              />
            </div>
          </div>
        </div>
        <div className="row justify-content-center">
          <p className="mb-0 col-12 col-md-11 text-danger text-right">
            {state.error}
          </p>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-outline-secondary py-0 px-3 mr-3"
          disabled={state.requestPending}
          onClick={props.toggleModal}
        >
          Cancel
        </button>
        <button
          className="btn modal-button-submit py-0 px-4"
          disabled={submitDisabled}
          onClick={handleRun}
        >
          {state.requestPending ? (
            <FontAwesomeIcon icon={faCircleNotch} spin />
          ) : (
            'Run'
          )}
        </button>
      </ModalFooter>
      <style jsx>{`
        .btn {
          font-size: 0.75rem;
        }
      `}</style>
    </Modal>
  );
}

RerunOutputSignalsModal.propTypes = {
  handleOutputSignalRerunSuccess: PropTypes.func,
  isOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
};

function validateDays({ endDate, startDate }) {
  if (isSameDay(startDate, endDate)) {
    throw new Error('Start and End Date cannot be the same day');
  }

  const validInterval = isWithinInterval(endDate, {
    end: addDays(startDate, maxDays),
    start: startDate,
  });
  if (!validInterval) {
    throw new Error(
      'Maximum of 30 days allowed. Please adjust Start or End Date'
    );
  }

  return { endDate: endDate.toISOString(), startDate: startDate.toISOString() };
}
